import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './PageCommon.css';
import './Profile.css';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Caracas',
  'Europe/Madrid',
  'Europe/London',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

function parsePlan(raw) {
  try {
    return typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};
  } catch {
    return {};
  }
}

export default function Profile() {
  const { lang } = useLang();
  const { user, fetchUser, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPass, setSavingPass] = useState(false);
  const [passMsg, setPassMsg] = useState(null);

  const [billingLoading, setBillingLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [deleteStep1, setDeleteStep1] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setMobile(user.mobile_with_country_code || '');
      setTimezone(user.timezone || 'America/New_York');
    }
  }, [user]);

  const plan = parsePlan(user?.plan);
  const planLabel = plan?.title || plan?.name || (lang === 'en' ? 'Free' : 'Gratis');

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      const r = await api.post('/user/update_profile', {
        name,
        email,
        mobile,
        mobile_with_country_code: mobile,
        timezone,
      });
      if (r.data?.success) {
        setProfileMsg({ type: 'ok', text: r.data?.msg || (lang === 'en' ? 'Saved.' : 'Guardado.') });
        await fetchUser();
      } else {
        setProfileMsg({
          type: 'err',
          text: r.data?.msg || (lang === 'en' ? 'Could not save.' : 'No se pudo guardar.'),
        });
      }
    } catch {
      setProfileMsg({ type: 'err', text: lang === 'en' ? 'Connection error.' : 'Error de conexión.' });
    }
    setSavingProfile(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassMsg(null);
    if (newPassword !== confirmPassword) {
      setPassMsg({
        type: 'err',
        text: lang === 'en' ? 'New passwords do not match.' : 'Las contraseñas nuevas no coinciden.',
      });
      return;
    }
    setSavingPass(true);
    try {
      const r = await api.post('/user/change_password', {
        currentPassword,
        newPassword,
      });
      if (r.data?.success) {
        setPassMsg({
          type: 'ok',
          text: r.data?.msg || (lang === 'en' ? 'Password updated.' : 'Contraseña actualizada.'),
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPassMsg({
          type: 'err',
          text: r.data?.msg || (lang === 'en' ? 'Could not update password.' : 'No se pudo actualizar.'),
        });
      }
    } catch {
      setPassMsg({ type: 'err', text: lang === 'en' ? 'Connection error.' : 'Error de conexión.' });
    }
    setSavingPass(false);
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const r = await api.post('/rt/billing-portal');
      if (r.data?.success && r.data?.url) window.location.href = r.data.url;
      else alert(r.data?.msg || '');
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setBillingLoading(false);
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        lang === 'en'
          ? 'Cancel at the end of the billing period?'
          : '¿Cancelar al final del periodo de facturación?',
      )
    ) {
      return;
    }
    setCancelLoading(true);
    try {
      const r = await api.post('/rt/cancel-subscription');
      alert(r.data?.msg || '');
      if (r.data?.success) await fetchUser();
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setCancelLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!deleteStep1) {
      if (
        window.confirm(
          lang === 'en'
            ? 'This permanently deletes your account and data. Continue?'
            : 'Esto eliminará tu cuenta y datos de forma permanente. ¿Continuar?',
        )
      ) {
        setDeleteStep1(true);
      }
      return;
    }
    const typed = window.prompt(
      lang === 'en' ? 'Type DELETE to confirm:' : 'Escribe DELETE para confirmar:',
    );
    if (typed !== 'DELETE') {
      setDeleteStep1(false);
      return;
    }
    setDeleteLoading(true);
    try {
      const r = await api.post('/rt/delete-account', { confirmation: 'DELETE' });
      if (r.data?.success) {
        logout();
        navigate('/login');
      } else {
        alert(r.data?.msg || (lang === 'en' ? 'Could not delete account.' : 'No se pudo eliminar.'));
      }
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setDeleteLoading(false);
    setDeleteStep1(false);
  };

  return (
    <div className="page profile">
      <div className="profile__header">
        <h1>{lang === 'en' ? 'Profile' : 'Perfil'}</h1>
        <p>{lang === 'en' ? 'Manage your account and subscription' : 'Administra tu cuenta y suscripción'}</p>
      </div>

      <section className="profile__section">
        <h2>{lang === 'en' ? 'Personal information' : 'Información personal'}</h2>
        <form onSubmit={handleSaveProfile}>
          <div className="profile__field">
            <label htmlFor="profile-name">{lang === 'en' ? 'Name' : 'Nombre'}</label>
            <input
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="profile__field">
            <label htmlFor="profile-email">Email</label>
            <input
              id="profile-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="profile__field">
            <label htmlFor="profile-phone">{lang === 'en' ? 'Phone' : 'Teléfono'}</label>
            <input
              id="profile-phone"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+1..."
              required
            />
          </div>
          <div className="profile__field">
            <label htmlFor="profile-tz">{lang === 'en' ? 'Timezone' : 'Zona horaria'}</label>
            <select
              id="profile-tz"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="profile__btn profile__btn--primary" disabled={savingProfile}>
            {savingProfile ? '...' : lang === 'en' ? 'Save changes' : 'Guardar cambios'}
          </button>
        </form>
        {profileMsg && (
          <div
            className={`profile__msg ${profileMsg.type === 'ok' ? 'profile__msg--ok' : 'profile__msg--err'}`}
          >
            {profileMsg.text}
          </div>
        )}
      </section>

      <section className="profile__section">
        <h2>{lang === 'en' ? 'Change password' : 'Cambiar contraseña'}</h2>
        <form onSubmit={handleChangePassword}>
          <div className="profile__field">
            <label htmlFor="pw-current">{lang === 'en' ? 'Current password' : 'Contraseña actual'}</label>
            <input
              id="pw-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="profile__field">
            <label htmlFor="pw-new">{lang === 'en' ? 'New password' : 'Nueva contraseña'}</label>
            <input
              id="pw-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <div className="profile__field">
            <label htmlFor="pw-confirm">{lang === 'en' ? 'Confirm new password' : 'Confirmar contraseña'}</label>
            <input
              id="pw-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <button type="submit" className="profile__btn profile__btn--primary" disabled={savingPass}>
            {savingPass ? '...' : lang === 'en' ? 'Update password' : 'Actualizar contraseña'}
          </button>
        </form>
        {passMsg && (
          <div className={`profile__msg ${passMsg.type === 'ok' ? 'profile__msg--ok' : 'profile__msg--err'}`}>
            {passMsg.text}
          </div>
        )}
      </section>

      <section className="profile__section">
        <h2>{lang === 'en' ? 'Subscription' : 'Suscripción'}</h2>
        {user?.subscription_status === 'active' && (
          <>
            <p className="profile__danger-note" style={{ color: 'var(--text-secondary)' }}>
              {lang === 'en' ? 'Plan:' : 'Plan:'} <strong>{planLabel}</strong>
            </p>
            <div className="profile__sub-actions">
              <button type="button" disabled={billingLoading} onClick={handleManageBilling}>
                {billingLoading ? '...' : lang === 'en' ? 'Manage Billing' : 'Gestionar facturación'}
              </button>
              <button type="button" disabled={cancelLoading} onClick={handleCancelSubscription}>
                {cancelLoading ? '...' : lang === 'en' ? 'Cancel Subscription' : 'Cancelar suscripción'}
              </button>
            </div>
          </>
        )}
        {user?.subscription_status === 'cancelling' && (
          <p className="profile__danger-note" style={{ color: 'var(--accent-yellow)' }}>
            {lang === 'en'
              ? 'Your subscription will end at the end of the billing period.'
              : 'Tu suscripción finalizará al terminar el periodo de facturación.'}
          </p>
        )}
        {user?.subscription_status !== 'active' && user?.subscription_status !== 'cancelling' && (
          <p>
            {lang === 'en' ? 'Plan:' : 'Plan:'}{' '}
            <strong>{planLabel}</strong>
            {' — '}
            <Link to="/app/tokens" className="profile__link">
              {lang === 'en' ? 'Upgrade plan' : 'Mejorar plan'}
            </Link>
          </p>
        )}
      </section>

      <section className="profile__section profile__danger">
        <h2>{lang === 'en' ? 'Danger zone' : 'Zona de peligro'}</h2>
        <p className="profile__danger-note">
          {lang === 'en'
            ? 'Permanently delete your account, subscription, and associated data. This cannot be undone.'
            : 'Elimina permanentemente tu cuenta, suscripción y datos asociados. No se puede deshacer.'}
        </p>
        <button
          type="button"
          className="profile__btn profile__btn--danger"
          disabled={deleteLoading}
          onClick={handleDeleteAccount}
        >
          {deleteLoading
            ? '...'
            : deleteStep1
              ? lang === 'en'
                ? 'Type DELETE in the prompt'
                : 'Escribe DELETE en el cuadro'
              : lang === 'en'
                ? 'Delete account'
                : 'Eliminar cuenta'}
        </button>
      </section>
    </div>
  );
}
